import * as React from "react";
import { cn } from "../../lib/utils";

export const SidebarContext = React.createContext<{
	expanded: boolean;
	setExpanded: React.Dispatch<React.SetStateAction<boolean>>;
} | null>(null);

export function SidebarProvider({
	children,
	defaultOpen = true,
}: {
	children: React.ReactNode;
	defaultOpen?: boolean;
}) {
	const [expanded, setExpanded] = React.useState(defaultOpen);

	return (
		<SidebarContext.Provider value={{ expanded, setExpanded }}>
			{children}
		</SidebarContext.Provider>
	);
}

export function Sidebar({
	className,
	children,
	collapsible = "icon",
	side = "left",
}: {
	className?: string;
	children: React.ReactNode;
	collapsible?: "icon" | "full" | false;
	side?: "left" | "right";
}) {
	const { expanded } = React.useContext(SidebarContext) || {};

	return (
		<aside
			className={cn(
				"h-screen sticky top-0",
				expanded ? "w-64" : "w-16",
				side === "left" ? "border-r" : "border-l",
				"transition-all duration-300 flex-shrink-0",
				className,
			)}
		>
			{children}
		</aside>
	);
}

export function SidebarHeader({
	children,
	className,
}: { children: React.ReactNode; className?: string }) {
	const { expanded } = React.useContext(SidebarContext) || {};

	return (
		<div
			className={cn("p-4", expanded ? "text-left" : "text-center", className)}
		>
			{children}
		</div>
	);
}

export function SidebarContent({ children }: { children: React.ReactNode }) {
	return <div className="px-3 py-2 flex-1">{children}</div>;
}

export function SidebarMenu({ children }: { children: React.ReactNode }) {
	return <nav className="space-y-2">{children}</nav>;
}

export function SidebarMenuItem({
	children,
	className,
	title,
}: {
	children: React.ReactNode;
	title: string;
	className?: string;
}) {
	return (
		<div title={title} className={className}>
			{children}
		</div>
	);
}

export function SidebarMenuButton({
	children,
	className,
	icon,
	...props
}: React.ComponentPropsWithoutRef<"button"> & { icon: React.ReactNode }) {
	const { expanded } = React.useContext(SidebarContext) || {};

	return (
		<button
			className={cn(
				"flex items-center w-full p-2 rounded-lg hover:bg-gray-100 transition-colors",
				expanded ? "justify-start space-x-3" : "justify-center",
				className,
			)}
			{...props}
		>
			{icon}
			{expanded && <span>{children}</span>}
		</button>
	);
}

export function SidebarTrigger({ className }: { className?: string }) {
	const { expanded, setExpanded } = React.useContext(SidebarContext) || {};

	if (setExpanded === undefined) return null;

	return (
		<button
			type="button"
			onClick={() => setExpanded((prev) => !prev)}
			className={cn(
				"fixed bottom-4 left-4 p-2 bg-gray-200 rounded-full hover:bg-gray-300 transition-colors z-50",
				className,
			)}
		>
			{expanded ? "←" : "→"}
		</button>
	);
}

export function SidebarGroup({
	children,
	title,
	icon,
	defaultOpen = true,
}: {
	children: React.ReactNode;
	title: string;
	icon: React.ReactNode;
	defaultOpen?: boolean;
}) {
	const [isOpen, setIsOpen] = React.useState(defaultOpen);
	const { expanded } = React.useContext(SidebarContext) || {};

	return (
		<div className="mb-2">
			<button
				type="button"
				onClick={() => setIsOpen(!isOpen)}
				className={cn(
					"flex items-center w-full p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-all duration-200",
					expanded ? "justify-between" : "justify-center",
					"text-sm font-medium text-gray-700 dark:text-gray-300",
				)}
				title={title}
			>
				<div className="flex items-center space-x-3">
					{icon}
					{expanded && <span>{title}</span>}
				</div>
				{expanded && (
					<svg
						className={cn(
							"w-4 h-4 transition-transform duration-200",
							isOpen ? "rotate-180" : "",
						)}
						fill="none"
						stroke="currentColor"
						viewBox="0 0 24 24"
					>
						<path
							strokeLinecap="round"
							strokeLinejoin="round"
							strokeWidth={2}
							d="M19 9l-7 7-7-7"
						/>
					</svg>
				)}
			</button>
			<div
				className={cn(
					"overflow-hidden transition-all duration-200",
					isOpen ? "max-h-96" : "max-h-0",
					expanded ? "ml-3" : "",
				)}
			>
				<div className="pt-1 space-y-1">{children}</div>
			</div>
		</div>
	);
}

export function SidebarDivider() {
	return <hr className="my-3 border-gray-200 dark:border-gray-700" />;
}
